import React, { useMemo } from 'react';
import { Modal } from 'glints-aries/es/@next';
import { includes } from 'lodash-es';
import { MessageDescriptor } from 'react-intl';
import {
  createHtmlPortalNode,
  InPortal,
  OutPortal,
} from 'react-reverse-portal';

import { fetchMe } from '../../../actions/user/me';
import { useAppDispatch } from '../../../store';
import { WhatsappVerificationStep } from '../../WhatsappVerification/types';
import { useWhatsappVerificationContext } from '../../WhatsappVerification/WhatsappVerificationContext/WhatsappVerificationContext';
import { withWhatsappVerificationContextProvider } from '../../WhatsappVerification/WhatsappVerificationContext/withWhatsappVerificationContextProvider';
import { InputNumberStep } from './components/InputNumberStep/InputNumberStep';
import { InputOTPStep } from './components/InputOTPStep/InputOTPStep';
import { ModalFooter } from './components/ModalFooter/ModalFooter';
import { Variant } from './components/VerificationSuccessStep/constants';
import { VerificationSuccessStep } from './components/VerificationSuccessStep/VerificationSuccessStep';

interface WhatsappVerificationModalProps {
  isOpen: boolean;
  onClose: (step?: WhatsappVerificationStep) => void;
  initialPhoneNumber?: string;
  initialCallingCode?: string;
  successButtonText: MessageDescriptor;
  successStepVariant: Variant;
  closeButtonText:
    | MessageDescriptor
    | ((step: WhatsappVerificationStep) => MessageDescriptor);
  showCloseButton: boolean;
  headers: Record<WhatsappVerificationStep, React.ReactNode | null>;
}

export const WhatsappVerificationModal = ({
  isOpen,
  onClose,
  initialPhoneNumber,
  initialCallingCode,
  successButtonText,
  successStepVariant,
  closeButtonText,
  showCloseButton,
  headers,
}: WhatsappVerificationModalProps) => {
  const dispatch = useAppDispatch();
  const { currentStep, setCurrentStep } = useWhatsappVerificationContext();
  const inputNumberPortalNode = useMemo(() => createHtmlPortalNode(), []);

  const handleClose = async () => {
    if (currentStep === WhatsappVerificationStep.VerificationSuccess) {
      await dispatch(fetchMe() as any);
    }

    onClose(currentStep);
    setCurrentStep(WhatsappVerificationStep.InputNumber);
  };

  const hasDefaultHeaderPadding = !includes(
    [
      WhatsappVerificationStep.InputNumber,
      WhatsappVerificationStep.ConfirmWhatsappNumber,
    ],
    currentStep
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      header={headers[currentStep]}
      headerHasDefaultPadding={hasDefaultHeaderPadding}
      showCloseButton={showCloseButton}
      customActions={
        <ModalFooter
          handleClose={handleClose}
          successButtonText={successButtonText}
          closeButtonText={
            typeof closeButtonText === 'function'
              ? closeButtonText(currentStep)
              : closeButtonText
          }
        />
      }
    >
      <InPortal node={inputNumberPortalNode}>
        <InputNumberStep
          initialPhoneNumber={initialPhoneNumber}
          initialCallingCode={initialCallingCode}
        />
      </InPortal>
      <Choose>
        <When condition={currentStep === WhatsappVerificationStep.InputOTP}>
          <InputOTPStep />
        </When>
        <When
          condition={
            currentStep === WhatsappVerificationStep.VerificationSuccess
          }
        >
          <VerificationSuccessStep variant={successStepVariant} />
        </When>
        <Otherwise>
          <OutPortal node={inputNumberPortalNode} />
        </Otherwise>
      </Choose>
    </Modal>
  );
};

export const WhatsappVerificationModalWithContextProvider = React.memo(
  withWhatsappVerificationContextProvider(WhatsappVerificationModal)
);
